import { useEffect, useRef, useState } from 'react';
import { SearchableSelect } from './SearchableSelect.jsx';
import { DropdownFacet } from './DropdownFacet.jsx';
import { FacetList } from './FacetList.jsx';
import { searchPrograms } from '../api.js';

const NEEDS_DESTINATION = 'Choose University country first';

/**
 * The reference's own Duration vocabulary (option type 20) — matched against
 * `coursesWebsite.duration` as a case-insensitive substring at index time (see
 * duration_labels in transform.js), not a numeric month range. Fixed and
 * short, so it is listed here rather than round-tripped through /options; the
 * order is ascending by what each label actually means, not raw ids.
 */
const DURATION_LABELS = [
  '12 months', '1 Year', '24 months', '2 Years', '3 Years', '3.5 Years', '4 Years',
];

const SORTS = [
  ['relevance', 'Best match'],
  ['fee_asc', 'Tuition: low to high'],
  ['fee_desc', 'Tuition: high to low'],
  ['duration_asc', 'Duration: shortest'],
  ['name_asc', 'Name: A–Z'],
];

/** "Any" / one value / "N selected" — the summary line every dropdown shows closed. */
function summaryForValues(selected) {
  if (selected.length === 0) return 'Any';
  if (selected.length === 1) return selected[0];
  return `${selected.length} selected`;
}

/**
 * Header counts come from the live facets, not from the catalogue-wide option
 * lists loaded at startup.
 *
 * "United Kingdom (39,497)" used to stay 39,497 no matter what else was
 * filtered - a number describing a search nobody is running. The server
 * measures each facet with its own filter lifted, so choosing one value does not
 * report every alternative as empty and strand the filter on its first choice.
 *
 * A zero option is disabled UNLESS it is the current selection: removing the
 * ground under a live selection makes the filter impossible to clear from its
 * own control.
 *
 * The magnitudes themselves are no longer shown next to each option — one live
 * total belongs on the page (the result bar above the table) and repeating a
 * per-option breakdown inside every open dropdown only competed with it. "Is
 * this zero" is still read from the facet, to grey out a value the current
 * search cannot return.
 */
function countsFrom(facet) {
  return new Map((facet ?? []).map((c) => [c.value, c.count]));
}

/**
 * Narrow a dependent option list to what the current search can actually
 * return, the way the reference's own option endpoints do — 18 raw course
 * types across the catalogue, 11 for the UK, 4 for Australia.
 *
 * `options` comes from /api/programs/options, which is measured over the
 * caller's entitlement scope and NOTHING else, so on its own it offers all 18
 * whatever destination is picked. The per-destination truth is in the search
 * response facets, so the list is intersected with those.
 *
 * When contextOptions is supplied (e.g. Course Intake), selecting an intake
 * preserves that valid context-derived option set instead of expanding to the
 * catalogue-wide list.
 */
function narrow(all, facet, selected) {
  const contextOptions = arguments[3] ?? null;
  if (contextOptions) {
    if (selected.length > 0) {
      const result = [...contextOptions];
      for (const selId of selected) {
        if (!result.some((o) => o.id === selId || o.value === selId)) {
          const found = (all ?? []).find((o) => o.id === selId || o.value === selId);
          if (found) result.push(found);
        }
      }
      return result;
    }
    return contextOptions;
  }
  const list = all ?? [];
  if (selected.length > 0) return list;
  const counts = new Map((facet ?? []).map((c) => [c.value, c.count]));
  if (counts.size === 0) return list;
  return list
    .filter((o) => (counts.get(o.id ?? o.value) ?? 0) > 0)
    .map((o) => ({ ...o, count: counts.get(o.id ?? o.value) }));
}

/**
 * The small "what does this do" control beside the Active status toggle.
 *
 * Replaces the old always-on banner: nothing appears until the eye is
 * clicked, and the explanation is one short sentence in a popover rather than
 * a strip of page-width real estate above "Search in Programs". Click the eye
 * again, or anywhere outside it, to close — it never opens itself, including
 * when the toggle is switched on.
 */
function ActiveStatusInfo() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="active-status-info" ref={ref}>
      <button
        type="button"
        className="active-status-info-btn"
        aria-label="What does Active status do?"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      {open && (
        <div className="active-status-info-popover" role="tooltip">
          Shows only programs with currently active intake periods available for search.
        </div>
      )}
    </div>
  );
}

export function SearchBar({ title = 'Search in Programs', filters, sort, options, facets, loading = false, dispatch }) {
  // First load: `/api/programs/options` has not answered yet, so every list is
  // empty. `searchPending` additionally covers a dependent list (Course Type,
  // Course Intake) being rebuilt after a parent filter moved.
  const optionsLoading = !options;
  const searchPending = optionsLoading || loading;
  const countryCounts = countsFrom(facets?.country_name);

  /**
   * Universities: the full catalogue-wide list (from /options), searchable, so
   * any of the ~610 can be reached — not just the top slice of the current
   * result set. Counts come from the live `university_name` facet.
   *
   * Unlike Course Type and Course Intake, this is NOT gated on a study
   * destination — the reference's own University picker works with no
   * destination chosen (picking a university narrows the search on its own,
   * the same way picking a country does).
   *
   * While nothing is picked the list is narrowed to universities still
   * reachable under whatever else is filtered; once a selection exists the
   * full list is shown again so more can be added, and a picked-but-zero-count
   * value is always kept so it stays clearable from its own control.
   */
  const universityCounts = countsFrom(facets?.university_name);
  const destinationUniversitiesMapRef = useRef(new Map());

  const country = filters.countries[0] ?? '';
  const destinationChosen = filters.countries.length > 0;

  const universityOptions = (() => {
    const all = (options?.universities ?? []).map((u) => ({
      value: u.value,
      label: u.value,
      count: universityCounts.get(u.value) ?? 0,
    }));
    if (destinationChosen && filters.universities.length === 0 && universityCounts.size > 0) {
      const valid = all.filter((o) => o.count > 0);
      if (valid.length > 0) {
        destinationUniversitiesMapRef.current.set(country, valid);
      }
    }
    if (destinationChosen) {
      const destUnis = destinationUniversitiesMapRef.current.get(country);
      if (destUnis && destUnis.length > 0) {
        const list = [...destUnis];
        for (const picked of filters.universities) {
          if (!list.some((o) => o.value === picked)) {
            list.push({ value: picked, label: picked, count: universityCounts.get(picked) ?? 0 });
          }
        }
        return list;
      }
      if (universityCounts.size > 0) {
        return all.filter((o) => o.count > 0 || filters.universities.includes(o.value));
      }
    }
    if (filters.universities.length > 0) {
      for (const picked of filters.universities) {
        if (!all.some((o) => o.value === picked)) all.push({ value: picked, label: picked, count: 0 });
      }
      return all;
    }
    if (universityCounts.size === 0) return all;
    return all.filter((o) => o.count > 0);
  })();

  /**
   * Course Type and Course Intake (exact) — the reference's own filter
   * vocabulary, revealed only once a study destination is chosen because their
   * option lists genuinely differ per destination. "January/February 2027" is
   * one exact intake with its own id — it is not month + year recombined.
   */
  // The reference filters Course Type on the NAME, not the option-1 id (see
  // `degreeLevels` in useProgramSearch.js) — `t.name` is both the value sent
  // and the label shown, so the control's own state already IS the reference's
  // vocabulary rather than an id needing a second lookup to display.
  const courseTypeCounts = countsFrom(facets?.course_level_id);
  const courseTypeContextKey = [
    filters.countries.slice().sort().join(','),
    filters.nationality ?? '',
    filters.intakeIds.slice().sort().join(','),
    filters.universities.slice().sort().join(','),
    filters.durationLabels.slice().sort().join(','),
    filters.englishTests.slice().sort().join(','),
    filters.scholarship ? '1' : '0',
    filters.freeToApply ? '1' : '0',
    filters.q ?? '',
  ].join('|');

  const contextCourseTypesMapRef = useRef(new Map());
  const destinationCourseTypesMapRef = useRef(new Map());
  const [, setCourseTypeUpdate] = useState(0);

  if (destinationChosen && filters.degreeLevels.length === 0 && courseTypeCounts.size > 0 && options?.courseTypes) {
    const valid = options.courseTypes
      .filter((t) => (courseTypeCounts.get(t.id) ?? 0) > 0)
      .map((t) => ({ ...t, count: courseTypeCounts.get(t.id) }));
    if (valid.length > 0) {
      contextCourseTypesMapRef.current.set(courseTypeContextKey, valid);
      if (!destinationCourseTypesMapRef.current.has(country)) {
        destinationCourseTypesMapRef.current.set(country, valid);
      }
    }
  }

  const contextCourseTypeOptions = destinationChosen
    ? (contextCourseTypesMapRef.current.get(courseTypeContextKey)
        ?? destinationCourseTypesMapRef.current.get(country)
        ?? null)
    : null;

  useEffect(() => {
    if (!destinationChosen) return;
    if (filters.degreeLevels.length > 0 && !contextCourseTypesMapRef.current.has(courseTypeContextKey)) {
      searchPrograms({
        ...filters,
        degreeLevels: [],
        courseTypes: [],
        perPage: 0,
      }).then((res) => {
        const counts = countsFrom(res.facets?.course_level_id);
        if (counts.size > 0 && options?.courseTypes) {
          const valid = options.courseTypes
            .filter((t) => (counts.get(t.id) ?? 0) > 0)
            .map((t) => ({ ...t, count: counts.get(t.id) }));
          if (valid.length > 0) {
            contextCourseTypesMapRef.current.set(courseTypeContextKey, valid);
            if (!destinationCourseTypesMapRef.current.has(country)) {
              destinationCourseTypesMapRef.current.set(country, valid);
            }
            setCourseTypeUpdate((v) => v + 1);
          }
        }
      }).catch(() => {});
    }
  }, [courseTypeContextKey, destinationChosen, country, filters, options?.courseTypes]);

  const courseTypeOptions = narrow(
    options?.courseTypes, facets?.course_level_id, filters.degreeLevels, contextCourseTypeOptions,
  ).map((t) => ({ value: t.name, label: t.name, count: t.count ?? 0 }));

  // "Active status" and the exact-intake picker are MUTUALLY EXCLUSIVE in this
  // UI: at most one of `intakeStatus === 'active'` and `intakeIds` may be
  // non-empty at a time (see the reducer — SET_ACTIVE_STATUS and the intakeIds
  // branch of TOGGLE in useProgramSearch.js). This is a UI-layer rule only; the
  // server still knows how to honour both together (buildFilterClauses matches
  // them on the same `courses.intakeCountry[]` entry), this app just never
  // sends that combination. The picker is gated on a study destination being
  // chosen, exactly like Course Type, AND on Active status being off. Its list
  // is narrowed on the live `intake_ids` facet while nothing is picked;
  // `o.active` is carried through for labelling but never filters the list.
  const activeStatusOn = filters.intakeStatus === 'active';

  // Context-derived intake options: the valid intakes for the current filter
  // context (destination + nationality + course type + university + other filters).
  // Selecting an intake must not reset or broaden to the global catalogue list.
  const filterContextKey = [
    filters.countries.slice().sort().join(','),
    filters.nationality ?? '',
    filters.degreeLevels.slice().sort().join(','),
    filters.universities.slice().sort().join(','),
    filters.durationLabels.slice().sort().join(','),
    filters.englishTests.slice().sort().join(','),
    filters.scholarship ? '1' : '0',
    filters.freeToApply ? '1' : '0',
    filters.q ?? '',
  ].join('|');

  const contextIntakesMapRef = useRef(new Map());
  const destinationIntakesMapRef = useRef(new Map());
  const [, setIntakeUpdate] = useState(0);

  const intakeCounts = countsFrom(facets?.intake_ids);
  if (destinationChosen && filters.intakeIds.length === 0 && intakeCounts.size > 0 && options?.intakeOptions) {
    const valid = options.intakeOptions
      .filter((o) => (intakeCounts.get(o.id) ?? 0) > 0)
      .map((o) => ({ ...o, count: intakeCounts.get(o.id) }));
    if (valid.length > 0) {
      contextIntakesMapRef.current.set(filterContextKey, valid);
      if (!destinationIntakesMapRef.current.has(country)) {
        destinationIntakesMapRef.current.set(country, valid);
      }
    }
  }

  const contextIntakeOptions = destinationChosen
    ? (contextIntakesMapRef.current.get(filterContextKey)
        ?? destinationIntakesMapRef.current.get(country)
        ?? null)
    : null;

  useEffect(() => {
    if (!destinationChosen) return;
    if (filters.intakeIds.length > 0 && !contextIntakesMapRef.current.has(filterContextKey)) {
      searchPrograms({
        ...filters,
        intakeIds: [],
        perPage: 0,
      }).then((res) => {
        const counts = countsFrom(res.facets?.intake_ids);
        if (counts.size > 0 && options?.intakeOptions) {
          const valid = options.intakeOptions
            .filter((o) => (counts.get(o.id) ?? 0) > 0)
            .map((o) => ({ ...o, count: counts.get(o.id) }));
          if (valid.length > 0) {
            contextIntakesMapRef.current.set(filterContextKey, valid);
            if (!destinationIntakesMapRef.current.has(country)) {
              destinationIntakesMapRef.current.set(country, valid);
            }
            setIntakeUpdate((v) => v + 1);
          }
        }
      }).catch(() => {});
    }
  }, [filterContextKey, destinationChosen, country, filters, options?.intakeOptions]);

  const exactIntakeOptions = narrow(
    options?.intakeOptions, facets?.intake_ids, filters.intakeIds, contextIntakeOptions,
  ).map((o) => ({ value: o.id, label: o.name, count: o.count ?? 0 }));

  const toggleMany = (key, next, current) => {
    const cur = new Set(current);
    const nxt = new Set(next);
    for (const v of nxt) if (!cur.has(v)) dispatch({ type: 'TOGGLE', key, value: v });
    for (const v of cur) if (!nxt.has(v)) dispatch({ type: 'TOGGLE', key, value: v });
  };

  // --- Duration / Quick filters / English proficiency, formerly the Refine
  // rail — now three more dropdowns in Row 3, the same DropdownFacet shell
  // (and the same dispatches) the rail used. See DropdownFacet.jsx.
  const englishCounts = new Map((facets?.english_tests ?? []).map((c) => [c.value, c.count]));
  const flagCount = (facet) => (facet ?? []).find((c) => c.value === 'true')?.count ?? 0;
  const quick = [
    ['scholarship', 'Scholarship available', flagCount(facets?.scholarship)],
    ['freeToApply', 'No application fee', flagCount(facets?.free_to_apply)],
  ];
  const quickActive = quick.filter(([key]) => filters[key]).length;

  const nationalityOptions = (options?.nationalities ?? [])
    .filter((n) => {
      if (options?.contractTerritories && Array.isArray(options.contractTerritories)) {
        return options.contractTerritories.map(String).includes(String(n.id));
      }
      return true;
    })
    .map((n) => ({ value: n.id, label: n.name }));

  return (
    <div className="searchbar">
      <div className="searchbar-row-keyword">
        {title && <h1 id="title" className="filterbar-title-inline">{title}</h1>}
        <div className="fld fld-keyword">
          <label htmlFor="q">Keyword</label>
          <div className="keyword-input-wrap">
            <span className="keyword-search-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              id="q"
              type="search"
              placeholder="e.g. business analytics"
              autoComplete="off"
              value={filters.q}
              onChange={(e) => dispatch({ type: 'SET_QUERY', value: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Row 2: University country | Student nationality | Course type */}
      <div className="searchbar-row-secondary">
        <SearchableSelect
          id="country"
          label="University country"
          placeholder="All countries"
          searchPlaceholder="Search destinations…"
          showCounts={false}
          // The parent filter: it must always be changeable, whatever a
          // dependent Course Type / Intake / University currently holds. See
          // the `disableZeroCount` doc comment in SearchableSelect.jsx.
          disableZeroCount={false}
          loading={optionsLoading}
          options={(options?.countries ?? []).map((c) => ({
            value: c.value,
            label: c.value,
            count: countryCounts.get(c.value) ?? 0,
          }))}
          value={country}
          onChange={(v) => dispatch({ type: 'SET_COUNTRY', value: v })}
        />

        <SearchableSelect
          id="nationality"
          label="Student nationality"
          placeholder="Any nationality"
          searchPlaceholder="Search nationalities…"
          showCounts={false}
          disableZeroCount={false}
          loading={optionsLoading}
          options={nationalityOptions}
          value={filters.nationality}
          onChange={(v) => dispatch({ type: 'SET_NATIONALITY', value: v })}
        />

        <SearchableSelect
          id="courseType"
          label="Course type"
          placeholder={destinationChosen ? 'Any course type' : NEEDS_DESTINATION}
          searchPlaceholder="Search course types…"
          multiple
          showCounts={false}
          disabled={!destinationChosen}
          loading={destinationChosen && searchPending}
          options={courseTypeOptions}
          value={filters.degreeLevels}
          onChange={(next) => toggleMany('degreeLevels', next, filters.degreeLevels)}
        />
      </div>

      {/* Row 3: University name | Duration | English proficiency | Quick
          filters | Course intake (exact) | Active status | Sort by */}
      <div className="searchbar-row-tertiary">
        <SearchableSelect
          id="university"
          label="University name"
          placeholder="All universities"
          searchPlaceholder="Search universities…"
          multiple
          showCounts={false}
          loading={optionsLoading}
          options={universityOptions}
          value={filters.universities}
          onChange={(next) => toggleMany('universities', next, filters.universities)}
        />

        <DropdownFacet
          id="fltDuration"
          label="Duration"
          active={filters.durationLabels.length > 0}
          summary={summaryForValues(filters.durationLabels)}
        >
          <FacetList
            items={DURATION_LABELS.map((label) => ({
              value: label,
              label,
              count: (facets?.duration_labels ?? []).find((c) => c.value === label)?.count ?? 0,
              selected: filters.durationLabels.includes(label),
              // OR semantics, same as English proficiency: any number of labels
              // may be on at once.
              onToggle: () => dispatch({ type: 'TOGGLE', key: 'durationLabels', value: label }),
            }))}
          />
        </DropdownFacet>

        <DropdownFacet
          id="fltEnglish"
          label="English proficiency"
          active={filters.englishTests.length > 0}
          summary={summaryForValues(filters.englishTests)}
        >
          <FacetList
            items={(options?.englishTests ?? []).map((value) => ({
              value,
              count: englishCounts.get(value) ?? 0,
              selected: filters.englishTests.includes(value),
              onToggle: () => dispatch({ type: 'TOGGLE', key: 'englishTests', value }),
            }))}
          />
        </DropdownFacet>

        <DropdownFacet
          id="fltQuick"
          label="Quick filters"
          active={quickActive > 0}
          summary={quickActive === 0 ? 'None' : `${quickActive} active`}
        >
          <FacetList
            items={quick.map(([key, label, count]) => ({
              value: key,
              label,
              count,
              selected: filters[key],
              onToggle: () => dispatch({ type: 'SET_FLAG', key, value: !filters[key] }),
            }))}
          />
        </DropdownFacet>

        <SearchableSelect
          id="courseIntake"
          label="Course intake (exact)"
          placeholder={
            !destinationChosen ? NEEDS_DESTINATION
              : activeStatusOn ? 'Disabled while Active status is on'
                : 'Any intake'
          }
          searchPlaceholder="Search intakes…"
          multiple
          showCounts={false}
          disabled={!destinationChosen || activeStatusOn}
          loading={destinationChosen && !activeStatusOn && searchPending}
          options={exactIntakeOptions}
          value={filters.intakeIds}
          onChange={(next) => toggleMany('intakeIds', next, filters.intakeIds)}
        />

        <div className="fld">
          <label htmlFor="activeStatus">Active status</label>
          <div className="active-status-field">
            <button
              type="button"
              id="activeStatus"
              role="switch"
              aria-checked={activeStatusOn}
              className="active-status-toggle"
              onClick={() => dispatch({ type: 'SET_ACTIVE_STATUS', value: !activeStatusOn })}
            >
              <span className="active-status-toggle-track" aria-hidden="true">
                <span className="active-status-toggle-thumb" />
              </span>
              <span className="active-status-toggle-state">{activeStatusOn ? 'On' : 'Off'}</span>
            </button>
            <ActiveStatusInfo />
          </div>
        </div>

        <div className="fld">
          <label htmlFor="sort">Sort by</label>
          <select
            id="sort"
            value={sort}
            onChange={(e) => dispatch({ type: 'SET_SORT', value: e.target.value })}
          >
            {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
